import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaChartLine,
  FaRupeeSign,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaSignInAlt,
  FaExclamationTriangle,
  FaSync,
  FaInfoCircle,
  FaArrowUp,
  FaArrowDown,
  FaCheckCircle,
  FaTimesCircle,
  FaQuoteLeft,
  FaQuoteRight,
  FaQuestionCircle,
  FaShieldAlt,
  FaBullhorn,
  FaBullseye,
  FaLightbulb,
  FaBalanceScale,
  FaHandshake,
  FaAccessibleIcon,
  FaTachometerAlt,
  FaGlobe,
  FaCrown
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import axios from 'axios';

const Nifty50 = () => {
  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [userData, setUserData] = useState(null);
  const [contentAccess, setContentAccess] = useState({ SEBIMonthlyRecommendations: 0 });

  // Page metadata========================================================
  const [pageMetadata, setPageMetadata] = useState({
    page_names: 'Market Sentiment Indicator (MSI)',
    description: 'Real-time Nifty 50 & Sensex valuation analysis'
  });
  // Add this fetch function (around line 60-80)
  const fetchPageMetadata = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/api/page-metadata/public/page10`);
      if (response.data) {
        setPageMetadata({
          page_names: response.data.page_name, // Using title from database
          description: response.data.description
        });
      }
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      // Keep default values if fetch fails
    }
  };

  // Add this inside your existing useEffect or create a new one (around line 100-110)
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.SEBIMonthlyRecommendations === 1) {
      fetchPageMetadata(); // Add this line
      fetchNiftyData(); // Your existing fetch
    }
  }, [isLoggedIn, checkingLogin, contentAccess.SEBIMonthlyRecommendations]);
  // Metadata End===========================================================

  
  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      const loggedIn = !!(accessToken && userDataStr);
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        const user = JSON.parse(userDataStr);
        setUserData(user);
        await fetchContentAccess(user.id);
      } else {
        setUserData(null);
        setContentAccess({ SEBIMonthlyRecommendations: 0 });
      }
      
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setCheckingLogin(false);
    }
  };
  
  const fetchContentAccess = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/content-access/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setContentAccess(response.data.data);
      } else {
        setContentAccess({ SEBIMonthlyRecommendations: 0 });
      }
    } catch (error) {
      console.error('Error fetching content access:', error);
      if (error.response?.status === 404) {
        setContentAccess({ SEBIMonthlyRecommendations: 0 });
      }
    }
  };
  
  useEffect(() => {
    checkLoginStatus();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  // ==================== END: LOGIN PROTECTION LOGIC ====================

  // ==================== COMPONENT STATE ====================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [processedData, setProcessedData] = useState({ 
    niftyData: {},
    sensexData: {},
    sentiment: '',
    sentimentColor: '',
    faqs: [],
    zones: [],
    quotes: [],
    disclaimers: [],
    generalContent: []
  });

  // API Configuration with Auto-refresh
  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1fz2UjsSid3aJab3LbZI2zbNLeqUkDuyPWmCsnTk0mLY/values/%27NIFTY%2050%27?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.SEBIMonthlyRecommendations === 1) {
      fetchNiftyData();
    } else {
      if (isLoggedIn && !checkingLogin) {
        setLoading(false);
      }
    }
  }, [isLoggedIn, checkingLogin, contentAccess.SEBIMonthlyRecommendations]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.SEBIMonthlyRecommendations === 1) {
      const intervalId = setInterval(() => {
        fetchNiftyData();
      }, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, checkingLogin, contentAccess.SEBIMonthlyRecommendations]);

  // Process data when data changes
  useEffect(() => {
    if (data && data.values) {
      const result = processData(data.values);
      setProcessedData(result);
    }
  }, [data]);

  const fetchNiftyData = async () => {
    try {
      setLoading(true);
      console.log('Fetching Nifty 50 Market Sentiment data...');
      
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Nifty 50 API Response received');
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      setData(result);
      setLastUpdated(new Date());
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching Nifty data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Process data from Google Sheets
  const processData = (values) => {
    const result = {
      niftyData: {},
      sensexData: {},
      sentiment: '',
      sentimentColor: 'red',
      faqs: [],
      zones: [],
      quotes: [],
      disclaimers: [],
      generalContent: [],
      marketingContent: []
    };

    if (!values) return result;

    let inFAQSection = false;
    let inDisclaimerSection = false;
    let inZoneSection = false;
    let foundMarketSentiment = false;

    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;

      // Extract NIFTY 50 data
      if (row[0] === 'NIFTY_50' && row[1] === 'NIFTY 50' && row[2]) {
        result.niftyData = {
          ticker: row[0],
          name: row[1],
          price: parseFloat(row[2]) || 0,
          formattedPrice: formatIndianCurrency(row[2])
        };
      }

      // Extract SENSEX data
      if (row[13] === 'Indexbom:sensex ' && row[14] === 'BSE SENSEX' && row[15]) {
        result.sensexData = {
          ticker: row[13],
          name: row[14],
          price: parseFloat(row[15]) || 0,
          formattedPrice: formatIndianCurrency(row[15])
        };
      }

      // Extract Market Sentiment
      if (!foundMarketSentiment && row[1] && row[1].includes('Market Sentiment Indicator [MSI] is indicating')) {
        const sentimentText = row[1];
        if (sentimentText.includes('Overvalued Zone')) {
          result.sentiment = 'OVERVALUED';
          result.sentimentColor = 'red';
        } else if (sentimentText.includes('slightly Overvalued Zone')) {
          result.sentiment = 'SLIGHTLY OVERVALUED';
          result.sentimentColor = 'yellow';
        } else if (sentimentText.includes('Undervalued Zone')) {
          result.sentiment = 'UNDERVALUED';
          result.sentimentColor = 'green';
        }
        foundMarketSentiment = true;
      }

      // Extract FAQ section
      if (row[1] && row[1].includes('FAQ')) {
        inFAQSection = true;
        inDisclaimerSection = false;
        inZoneSection = false;
        continue;
      }

      if (inFAQSection && row[1] && row[1].includes('Answer-')) {
        const faqMatch = row[1].match(/(\d+\.)(.+?)\nAnswer-\s*(.+)/s);
        if (faqMatch) {
          result.faqs.push({
            id: result.faqs.length + 1,
            question: faqMatch[2].trim(),
            answer: faqMatch[3].trim()
          });
        } else if (row[1].includes('?')) {
          // Handle multi-line FAQ entries
          const lastFaq = result.faqs[result.faqs.length - 1];
          if (lastFaq && row[1].trim()) {
            lastFaq.answer += '\n' + row[1].trim();
          }
        }
      }

      // Extract Zones information
      if (row[1] && row[1].includes('UNDERVALUED ZONE') || 
          row[1] && row[1].includes('SLIGHTLY OVERVALUED ZONE') || 
          row[1] && row[1].includes('Overvalued Zone')) {
        inZoneSection = true;
        const zoneText = row[1];
        if (zoneText.includes('UNDERVALUED ZONE')) {
          result.zones.push({
            name: 'UNDERVALUED ZONE',
            color: 'green',
            description: zoneText.split('UNDERVALUED ZONE [Green color]:')[1]?.trim() || zoneText
          });
        } else if (zoneText.includes('SLIGHTLY OVERVALUED ZONE')) {
          result.zones.push({
            name: 'SLIGHTLY OVERVALUED ZONE',
            color: 'yellow',
            description: zoneText.split('SLIGHTLY OVERVALUED ZONE[ Yellow color]:')[1]?.trim() || zoneText
          });
        } else if (zoneText.includes('Overvalued Zone')) {
          result.zones.push({
            name: 'OVERVALUED ZONE',
            color: 'red',
            description: zoneText.split('Overvalued Zone[Red color]:')[1]?.trim() || zoneText
          });
        }
      }

      // Extract Quotes
      if (row[2] && row[2].includes('Warren Buffet')) {
        result.quotes.push({
          text: row[2].split('-----')[0]?.trim(),
          author: 'Warren Buffet'
        });
      }

      // Extract Disclaimers
      if (row[1] && (row[1].includes('Disclaimer') || row[1].includes('PLEASE CONSULT YOUR FINANCIAL ADVISOR'))) {
        inDisclaimerSection = true;
        if (!row[1].includes('Disclaimer') && !row[1].includes('PLEASE CONSULT')) {
          result.disclaimers.push(row[1].trim());
        }
      }

      if (inDisclaimerSection && row[1] && row[1].includes('All the information')) {
        result.disclaimers.push(row[1].trim());
      }

      // Extract General Content (marketing, descriptions, etc.)
      if (row[3] && row[3].includes('Now Check Market Valuations')) {
        result.marketingContent.push({
          type: 'headline',
          content: row[3]
        });
      }

      if (row[1] && row[1].includes('MSI By Market Maestroo is 99.9%')) {
        result.generalContent.push({
          type: 'effectiveness',
          content: row[1]
        });
      }

      if (row[1] && row[1].includes('It describes the current valuations')) {
        result.generalContent.push({
          type: 'description',
          content: row[1]
        });
      }

      // End sections when we hit empty rows
      if (row.every(cell => !cell || cell.trim() === '')) {
        if (inFAQSection && result.faqs.length > 0) inFAQSection = false;
        if (inDisclaimerSection && result.disclaimers.length > 0) inDisclaimerSection = false;
      }
    }

    // If sentiment not found from indicator, determine from zones
    if (!result.sentiment && result.zones.length > 0) {
      // Check which zone has the most recent or prominent mention
      const lastZone = result.zones[result.zones.length - 1];
      result.sentiment = lastZone.name;
      result.sentimentColor = lastZone.color;
    }

    console.log('Processed data:', result);
    return result;
  };

  // Format Indian currency
  const formatIndianCurrency = (amount) => {
    if (!amount) return '₹0';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(num);
  };

  // Get sentiment icon
  const getSentimentIcon = (sentiment) => {
    switch(sentiment) {
      case 'OVERVALUED':
        return <FaTimesCircle className="text-red-500" />;
      case 'SLIGHTLY OVERVALUED':
        return <FaExclamationTriangle className="text-yellow-500" />;
      case 'UNDERVALUED':
        return <FaCheckCircle className="text-green-500" />;
      default:
        return <FaChartLine className="text-blue-500" />;
    }
  };

  // Get sentiment badge color
  const getSentimentBadgeColor = (sentiment) => {
    switch(sentiment) {
      case 'OVERVALUED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'SLIGHTLY OVERVALUED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'UNDERVALUED':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get zone color classes
  const getZoneColorClasses = (zoneName) => {
    switch(zoneName) {
      case 'UNDERVALUED ZONE':
        return {
          bg: 'bg-gradient-to-r from-green-50 to-emerald-50',
          border: 'border-green-200',
          text: 'text-green-800',
          icon: 'text-green-500'
        };
      case 'SLIGHTLY OVERVALUED ZONE':
        return {
          bg: 'bg-gradient-to-r from-amber-50 to-yellow-50',
          border: 'border-amber-200',
          text: 'text-amber-800',
          icon: 'text-amber-500'
        };
      case 'OVERVALUED ZONE':
        return {
          bg: 'bg-gradient-to-r from-red-50 to-pink-50',
          border: 'border-red-200',
          text: 'text-red-800',
          icon: 'text-red-500'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          icon: 'text-gray-500'
        };
    }
  };

  // Calculate gauge value based on price (for visual representation)
  const calculateGaugeValue = (price, indexType) => {
    if (!price) return 0.5; // Default middle position
    
    // Simplified gauge calculation - you can adjust these ranges based on actual data
    if (indexType === 'NIFTY') {
      const min = 20000;
      const max = 28000;
      const normalized = (price - min) / (max - min);
      return Math.max(0, Math.min(1, normalized || 0.5));
    } else { // SENSEX
      const min = 60000;
      const max = 90000;
      const normalized = (price - min) / (max - min);
      return Math.max(0, Math.min(1, normalized || 0.5));
    }
  };

  // Get gauge color based on sentiment
  const getGaugeColor = (sentiment) => {
    switch(sentiment) {
      case 'OVERVALUED':
        return 'text-red-500';
      case 'SLIGHTLY OVERVALUED':
        return 'text-yellow-500';
      case 'UNDERVALUED':
        return 'text-green-500';
      default:
        return 'text-blue-500';
    }
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  if (checkingLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access the Nifty 50 Market Sentiment Indicator.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <FaSignInAlt className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Nifty 50 Market Sentiment Indicator</h1>
                    <p className="text-purple-100">Real-time market valuation analysis tool</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content blurred for privacy. Please login to view.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (contentAccess.SEBIMonthlyRecommendations !== 1) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Access Restricted</h2>
                <p className="text-gray-600 mb-3">
                  Hello <span className="font-semibold text-blue-600">{userData?.username}</span>! 
                  You don't have access to the Nifty 50 Market Sentiment Indicator yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This premium content requires special access permissions.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+919319745997"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaPhone className="text-lg" />
                    Call Us: +91 9319745997
                  </a>
                  <a 
                    href="mailto:support@marketmaestroo.com"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaEnvelope className="text-lg" />
                    Email: support@marketmaestroo.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Nifty 50 Market Sentiment Indicator</h1>
                    <p className="text-purple-100">Real-time market valuation analysis tool</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content access restricted.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Market Sentiment Indicator...</p>
          <p className="text-sm text-gray-500 mt-2">Auto-refresh every 60 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchNiftyData}
            className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { niftyData, sensexData, sentiment, sentimentColor, faqs, zones, quotes, disclaimers, generalContent } = processedData;

  // Calculate gauge values
  const niftyGaugeValue = calculateGaugeValue(niftyData.price, 'NIFTY');
  const sensexGaugeValue = calculateGaugeValue(sensexData.price, 'SENSEX');

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{pageMetadata.page_names}</h1>
                <p className="text-purple-100">{pageMetadata.description}</p>
                <p className="text-purple-200 text-sm mt-1">Powered by Market Maestroo • 99.9% Accuracy</p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <FaTachometerAlt className="text-yellow-300" />
                  <span className="font-semibold">Live Market Indicator</span>
                </div>
                <button
                  onClick={fetchNiftyData}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                  <FaSync className="text-sm" />
                  Refresh Now
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Market Index Cards with Speedometers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* NIFTY 50 Card with Speedometer */}
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-6 shadow-lg border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">NIFTY 50</h3>
                    <p className="text-sm text-gray-600">National Stock Exchange</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FaGlobe className="text-2xl text-blue-600" />
                  </div>
                </div>
                
                {/* Speedometer Gauge for NIFTY 50 */}
                <div className="relative mb-4">
                  {/* Gauge Container */}
                  <div className="relative h-32 w-full flex items-center justify-center">
                    {/* Gauge Background */}
                    <div className="absolute w-56 h-28 rounded-t-full border-t-8 border-x-8 border-b-2 border-gray-200 bg-gray-50 overflow-hidden">
                      {/* Gauge Fill - Colored Arc */}
                      <div 
                        className="absolute h-24 w-56 -top-1"
                        style={{
                          background: `conic-gradient(
                            from 180deg at 50% 0%,
                            #10B981 0deg,
                            #FBBF24 ${90 * niftyGaugeValue}deg,
                            #EF4444 ${180 * niftyGaugeValue}deg,
                            #EF4444 180deg
                          )`,
                          clipPath: 'ellipse(50% 100% at 50% 0%)',
                          transform: `rotate(${180 * niftyGaugeValue}deg)`,
                          transformOrigin: '50% 100%',
                          transition: 'transform 1s ease-in-out'
                        }}
                      />
                      
                      {/* Gauge Center Mask */}
                      <div className="absolute w-40 h-20 bg-gray-50 rounded-t-full top-1 left-8"></div>
                      
                      {/* Gauge Marks */}
                      <div className="absolute top-0 left-0 w-full h-full">
                        {/* Center Mark */}
                        <div className="absolute top-0 left-1/2 w-1 h-4 bg-gray-400 transform -translate-x-1/2"></div>
                        {/* Left Mark */}
                        <div className="absolute top-4 left-4 w-1 h-3 bg-gray-300 rotate-45"></div>
                        {/* Right Mark */}
                        <div className="absolute top-4 right-4 w-1 h-3 bg-gray-300 -rotate-45"></div>
                      </div>
                    </div>
                    
                    {/* Needle */}
                    <div 
                      className="absolute bottom-8 left-1/2 w-1 h-24 origin-bottom"
                      style={{
                        transform: `translateX(-50%) rotate(${-90 + (180 * niftyGaugeValue)}deg)`,
                        transition: 'transform 1s ease-in-out'
                      }}
                    >
                      <div className="relative w-full h-full">
                        <div className={`absolute top-0 left-1/2 w-0.5 h-20 bg-gray-800 transform -translate-x-1/2 ${getGaugeColor(sentiment)}`}></div>
                        <div className="absolute bottom-0 left-1/2 w-3 h-3 rounded-full bg-gray-800 transform -translate-x-1/2"></div>
                        <div className="absolute bottom-1 left-1/2 w-2 h-2 rounded-full bg-white transform -translate-x-1/2"></div>
                      </div>
                    </div>
                    
                    {/* Current Price Display */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-center">
                      <div className="text-3xl font-bold text-gray-900">
                        {niftyData.formattedPrice || '₹26,176'}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">Current Price</div>
                    </div>
                  </div>
                  
                  {/* Gauge Labels */}
                  <div className="flex justify-between items-center mt-4 px-2">
                    <div className="text-left">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs font-medium text-gray-600">Undervalued</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Safe to Invest</div>
                    </div>
                    
                    <div className="text-center">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${getSentimentBadgeColor(sentiment)}`}>
                        {sentiment || 'OVERVALUED'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Market Zone</div>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <span className="text-xs font-medium text-gray-600">Overvalued</span>
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Risk Zone</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SENSEX Card with Speedometer */}
              <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl p-6 shadow-lg border border-green-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">BSE SENSEX</h3>
                    <p className="text-sm text-gray-600">Bombay Stock Exchange</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <FaAccessibleIcon className="text-2xl text-green-600" />
                  </div>
                </div>
                
                {/* Speedometer Gauge for SENSEX */}
                <div className="relative mb-4">
                  {/* Gauge Container */}
                  <div className="relative h-32 w-full flex items-center justify-center">
                    {/* Gauge Background */}
                    <div className="absolute w-56 h-28 rounded-t-full border-t-8 border-x-8 border-b-2 border-gray-200 bg-gray-50 overflow-hidden">
                      {/* Gauge Fill - Colored Arc */}
                      <div 
                        className="absolute h-24 w-56 -top-1"
                        style={{
                          background: `conic-gradient(
                            from 180deg at 50% 0%,
                            #10B981 0deg,
                            #FBBF24 ${90 * sensexGaugeValue}deg,
                            #EF4444 ${180 * sensexGaugeValue}deg,
                            #EF4444 180deg
                          )`,
                          clipPath: 'ellipse(50% 100% at 50% 0%)',
                          transform: `rotate(${180 * sensexGaugeValue}deg)`,
                          transformOrigin: '50% 100%',
                          transition: 'transform 1s ease-in-out'
                        }}
                      />
                      
                      {/* Gauge Center Mask */}
                      <div className="absolute w-40 h-20 bg-gray-50 rounded-t-full top-1 left-8"></div>
                      
                      {/* Gauge Marks */}
                      <div className="absolute top-0 left-0 w-full h-full">
                        {/* Center Mark */}
                        <div className="absolute top-0 left-1/2 w-1 h-4 bg-gray-400 transform -translate-x-1/2"></div>
                        {/* Left Mark */}
                        <div className="absolute top-4 left-4 w-1 h-3 bg-gray-300 rotate-45"></div>
                        {/* Right Mark */}
                        <div className="absolute top-4 right-4 w-1 h-3 bg-gray-300 -rotate-45"></div>
                      </div>
                    </div>
                    
                    {/* Needle */}
                    <div 
                      className="absolute bottom-8 left-1/2 w-1 h-24 origin-bottom"
                      style={{
                        transform: `translateX(-50%) rotate(${-90 + (180 * sensexGaugeValue)}deg)`,
                        transition: 'transform 1s ease-in-out'
                      }}
                    >
                      <div className="relative w-full h-full">
                        <div className={`absolute top-0 left-1/2 w-0.5 h-20 bg-gray-800 transform -translate-x-1/2 ${getGaugeColor(sentiment)}`}></div>
                        <div className="absolute bottom-0 left-1/2 w-3 h-3 rounded-full bg-gray-800 transform -translate-x-1/2"></div>
                        <div className="absolute bottom-1 left-1/2 w-2 h-2 rounded-full bg-white transform -translate-x-1/2"></div>
                      </div>
                    </div>
                    
                    {/* Current Price Display */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-center">
                      <div className="text-3xl font-bold text-gray-900">
                        {sensexData.formattedPrice || '₹85,063'}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">Current Price</div>
                    </div>
                  </div>
                  
                  {/* Gauge Labels */}
                  <div className="flex justify-between items-center mt-4 px-2">
                    <div className="text-left">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs font-medium text-gray-600">Undervalued</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Safe to Invest</div>
                    </div>
                    
                    <div className="text-center">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${getSentimentBadgeColor(sentiment)}`}>
                        {sentiment || 'OVERVALUED'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Market Zone</div>
                    </div>
                    
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <span className="text-xs font-medium text-gray-600">Overvalued</span>
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Risk Zone</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Market Sentiment Indicator */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200 mb-8">
              <div className="text-left mb-8">
                <div className="inline-flex items-left gap-3 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full flex items-center justify-center">
                    <FaBullhorn className="text-3xl text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Current Market Sentiment</h2>
                    <p className="text-gray-600">Based on MSI Analysis</p>
                  </div>
                </div>
              </div>

              {/* General Content from Sheets */}
              {generalContent.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-xl p-6 mb-4 border border-gray-100"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.type === 'effectiveness' ? (
                        <FaLightbulb className="text-xl text-blue-600" />
                      ) : (
                        <FaInfoCircle className="text-xl text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {item.content}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Three Zones Section */}
          {zones.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-12"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Understanding the Three Market Zones</h2>
                <p className="text-gray-600 max-w-3xl mx-auto">
                  The Market Sentiment Indicator categorizes market conditions into three distinct zones to guide your investment decisions.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                {zones.map((zone, index) => {
                  const colors = getZoneColorClasses(zone.name);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                      className={`rounded-2xl p-6 border-2 ${colors.bg} ${colors.border} h-full flex flex-col`}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-12 h-12 rounded-xl ${colors.bg.replace('gradient-to-r', '')} border ${colors.border} flex items-center justify-center`}>
                          {zone.name.includes('UNDERVALUED') ? (
                            <FaArrowDown className={`text-2xl ${colors.icon}`} />
                          ) : zone.name.includes('SLIGHTLY') ? (
                            <FaExclamationTriangle className={`text-2xl ${colors.icon}`} />
                          ) : (
                            <FaArrowUp className={`text-2xl ${colors.icon}`} />
                          )}
                        </div>
                        <div>
                          <h3 className={`font-bold text-lg ${colors.text}`}>
                            {zone.name.split('[')[0].trim()}
                          </h3>
                          <div className={`w-6 h-2 rounded-full ${colors.bg.split(' ')[0].replace('bg-', 'bg-')}`}></div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line text-sm">
                          {zone.description}
                        </p>
                      </div>
                      <div className="mt-6 pt-4 border-t border-gray-200/50">
                        <div className="flex items-center gap-2 text-sm">
                          <FaHandshake className={colors.icon} />
                          <span className={colors.text}>
                            {zone.name.includes('UNDERVALUED') ? 'Ideal for investment' :
                             zone.name.includes('SLIGHTLY') ? 'Invest with caution' :
                             'Avoid fresh investments'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Quotes Section */}
          {quotes.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-12"
            >
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200">
                <div className="text-center">
                  <FaQuoteLeft className="text-3xl text-amber-400 mx-auto mb-4" />
                  <blockquote className="text-xl italic text-gray-800 mb-4 max-w-3xl mx-auto">
                    "{quotes[0]?.text || 'Be Fearful when others are Greedy & Greedy when others are Fearful'}"
                  </blockquote>
                  <FaQuoteRight className="text-3xl text-amber-400 mx-auto mb-2" />
                  <cite className="text-gray-600 font-semibold">
                    — {quotes[0]?.author || 'Warren Buffet'}
                  </cite>
                </div>
              </div>
            </motion.div>
          )}

          {/* FAQ Section */}
          {faqs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mb-12"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FaQuestionCircle className="text-2xl text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
                    <p className="text-gray-600">Common queries about Market Sentiment Indicator</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {faqs.map((faq, index) => (
                    <motion.div
                      key={faq.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-50 rounded-xl p-6 border border-gray-100"
                    >
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="font-bold text-blue-600">{faq.id}</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-2 text-lg">
                            {faq.question}
                          </h3>
                          <div className="bg-white rounded-lg p-4">
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Disclaimers Section */}
          {disclaimers.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-8"
            >
              <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-8 border-2 border-red-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <FaShieldAlt className="text-2xl text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Important Disclaimers</h2>
                    <p className="text-gray-600">Please read carefully before making investment decisions</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {disclaimers.map((disclaimer, index) => (
                    <div
                      key={index}
                      className="bg-white/70 rounded-xl p-5 border border-red-100"
                    >
                      <p className="text-red-700 leading-relaxed whitespace-pre-line text-sm">
                        {disclaimer}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-red-200">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 bg-red-100 text-red-800 px-4 py-2 rounded-full">
                      <FaCrown className="text-red-600" />
                      <span className="font-semibold">PLEASE CONSULT YOUR FINANCIAL ADVISOR BEFORE INVESTMENT</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Footer */}
          <div className="text-center text-sm text-gray-500">
            {lastUpdated && (
              <p className="mt-1">Last updated: {lastUpdated.toLocaleString()}</p>
            )}
            <div className="mt-4 flex flex-col md:flex-row items-center justify-center gap-4">
              <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
                <FaBullseye className="text-purple-500" />
                <span className="font-medium text-gray-700">Valuation Model Powered By Market Maestroo</span>
              </div>
              <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full">
                <FaChartLine className="text-blue-500" />
                <span className="font-medium text-gray-700">99.9% Effective in Market Valuation</span>
              </div>
            </div>
          </div>
        </div>
        
      </div>
      <Footer />
    </>
  );
};

export default Nifty50;